import { useApplyEdition } from '@/pages/Apply/components/use-apply-edition';
import { router } from '@inertiajs/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { ApplyTeamSetupsShow } from '@/types';
import { errorMessages } from '../components/errors';
import { EventLabel } from '../components/EventLabel';
import { LogoutLink } from '../components/LogoutLink';

type Modality = 'team' | 'team_looking';

const SIZE_OPTIONS = [3, 4, 5] as const;
type Size = (typeof SIZE_OPTIONS)[number];

// app/(public)/${eventSlug}/apply/team-setup/page.tsx + team-setup-form.tsx.
export default function Show({
  initialModality,
  hasSlots,
  leaderName,
}: ApplyTeamSetupsShow) {
  const { eventSlug } = useApplyEdition();
  const [isPending, setIsPending] = useState(false);

  // Jump straight to the team form only when the team is actually configured
  // (modality='team' AND slots already exist). For everything else — brand-new
  // drafts whose default modality is 'team' but with no slots, team_looking,
  // or solo — start on the modality picker.
  const [modality, setModality] = useState<Modality | null>(
    initialModality === 'team' && hasSlots ? 'team' : null,
  );
  const [size, setSize] = useState<Size>(3);
  const [names, setNames] = useState<string[]>(['', '']);

  const updateSize = (next: Size) => {
    setSize(next);
    setNames((prev) => {
      const needed = next - 1;
      if (prev.length === needed) return prev;
      if (prev.length < needed) {
        return [...prev, ...Array(needed - prev.length).fill('')];
      }
      return prev.slice(0, needed);
    });
  };

  const updateName = (idx: number, value: string) => {
    setNames((prev) => prev.map((n, i) => (i === idx ? value : n)));
  };

  const save = (payload: { modality: Modality; placeholderNames?: string[] }) => {
    setIsPending(true);
    router.post(`/${eventSlug}/apply/team-setup`, payload, {
      preserveState: true,
      onError: (errors) =>
        toast.error(
          errorMessages(errors).base ?? 'No se pudo guardar el equipo',
        ),
      onFinish: () => setIsPending(false),
    });
  };

  const submitLooking = () => save({ modality: 'team_looking' });

  const submitTeam = () => {
    const trimmed = names.map((n) => n.trim());
    if (trimmed.some((n) => n.length === 0)) {
      toast.error('Completa el nombre de cada compañero');
      return;
    }
    save({ modality: 'team', placeholderNames: trimmed });
  };

  return (
    <>
      <LogoutLink />
      <div className="mx-auto max-w-xl space-y-8 px-4 py-12">
        <header className="space-y-2">
          <p className="text-muted-foreground text-sm">
            Postulación a <EventLabel />
          </p>
          <h1 className="font-bold text-2xl uppercase tracking-wide sm:text-3xl">
            TEAM.READY?
          </h1>
        </header>

        {modality === null && (
          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceCard
              label="Ya tengo equipo"
              description="Conozco a las personas con las que voy a hackear."
              onClick={() => setModality('team')}
            />
            <ChoiceCard
              label="Busco equipo"
              description="Formarás equipo al inicio del evento."
              onClick={submitLooking}
              disabled={isPending}
            />
          </div>
        )}

        {modality === 'team' && (
          <div className="space-y-6 border-2 border-primary bg-background/80 p-6">
            <div className="space-y-2">
              <Label>Tamaño del equipo (incluyéndote)</Label>
              <div className="flex gap-2">
                {SIZE_OPTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => updateSize(s)}
                    disabled={isPending}
                    className={cn(
                      'flex h-10 w-12 items-center justify-center border-2 font-bold',
                      size === s
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background text-foreground hover:border-primary',
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Nombres del equipo</Label>
              <div className="space-y-1.5">
                <span className="block text-muted-foreground text-xs">
                  Hacker 1 (tú)
                </span>
                <Input value={leaderName} readOnly disabled />
              </div>
              {names.map((name, idx) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key -- stable size-bounded list
                  key={idx}
                  className="space-y-1.5"
                >
                  <span className="block text-muted-foreground text-xs">
                    Hacker {idx + 2}
                  </span>
                  <Input
                    value={name}
                    onChange={(e) => updateName(idx, e.target.value)}
                    placeholder={`Nombre del hacker ${idx + 2}`}
                    disabled={isPending}
                  />
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => setModality(null)}
                disabled={isPending}
                className="flex-1"
              >
                Volver
              </Button>
              <Button
                onClick={submitTeam}
                disabled={isPending}
                className="flex-1"
              >
                {isPending ? 'Guardando...' : 'Generar invitaciones'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ChoiceCard({
  label,
  description,
  onClick,
  disabled,
}: {
  label: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col gap-2 border-2 border-primary bg-background/80 p-6 text-left transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="font-bold uppercase tracking-wide">{label}</span>
      <span className="text-muted-foreground text-sm">{description}</span>
    </button>
  );
}
